/**
 * @packageDocumentation
 * @module f1/security
 */

import to_string from '../to/to_string';

export type t_digest_alg = 'SHA-256' | 'SHA-512';

/**
 * @param message any object that can be stringified
 */
export type DigestValue = (message: unknown) => Promise<string>;

export interface Digest {
  /**
   * Create a unique hash of an object or value. This is useful for change detection or in-memory security assessments
   *
   * @public
   * @function f1.digest
   * @param cryptoAlgorithm only one digest is currently supported (SHA-256)
   * @param message any object that can be stringified
   * @example
   *
   * ```ts
   * f1.digest('SHA-256', {
   *   username: 'abcde'
   * }) => e26ba118d92b692e3e8b136518072cd97442138e951fc978904d434f65d54e90
   * ```
   */
  (cryptoAlgorithm: t_digest_alg, message: unknown): Promise<string>;
  /**
   * {@link FnDigestValue}
   * @param cryptoAlgorithm only one digest is currently supported (SHA-256)
   *
   */
  (cryptoAlgorithm: t_digest_alg): DigestValue;
}

/**
 * @see Digest
 */
export default async (cryptoAlgorithm: t_digest_alg, message: unknown): Promise<string> => {
  // @todo allow the digest algorithm to change
  const hashBuffer = await crypto.subtle.digest(cryptoAlgorithm, new TextEncoder().encode(to_string(message)));
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};
